import {
  fetchOrganizationApplicationsCSV,
  getOrganizationApplications,
} from 'admin/actions/merchant-applications';
import { canManageThirdPartyIssuerOrg } from 'admin/authorities';
import { Button } from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import Select from 'admin/components/atoms/Select';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { downloadCsvFile, toQueryParams } from 'admin/utils';
import type { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import DateTimeRange from '../../atoms/DateTimeRange';
import Headings from '../../atoms/Headings';
import Input from '../../atoms/Input';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ListTable from './ListTable';

type GetOrganizationApplicationsResponse = Awaited<ReturnType<typeof getOrganizationApplications>>;

type StatusOption = {
  label: string;
  value: string | null;
};

const STATUS_OPTIONS: StatusOption[] = [
  { label: '指定なし', value: null },
  { label: '申請受付済', value: 'submitted' },
  { label: '審査中', value: 'reviewing' },
  { label: '保留中', value: 'pending' },
  { label: '申請承認済', value: 'approved' },
  { label: '却下', value: 'rejected' },
];

const Wrapper = styled.div``;

const Row = styled.div`
  padding: 10px 0;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #333;
  white-space: nowrap;
`;

const FilterItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${Label} {
    margin-bottom: 0;
  }
`;

const SelectWrapper = styled.div`
  width: 200px;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '決済加盟店管理', href: '', isCurrentPage: true },
  { value: '決済加盟店申請管理', href: '', isCurrentPage: true },
];

const PageHeader = styled(Headings.h5)`
  margin-bottom: 8px;
`;

const NotAllowedMessage = styled.p`
  margin-top: 24px;
  padding: 16px;
`;

const SearchResultInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;
const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  margin-left: 15px;
`;
const SearchResultCount = styled.span`
  flex: 1;
`;

const MerchantApplications = () => {
  const user = useAppSelector((state) => state.user);
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const isAllowed = canManageThirdPartyIssuerOrg({ user, selectedPrivateMoney });
  const [applications, setApplications] = useState<GetOrganizationApplicationsResponse | null>(
    null
  );

  // UI state (what the user is currently editing in the filter form)
  const [selectedStatus, setSelectedStatus] = useState<StatusOption>(STATUS_OPTIONS[0]);
  const [uuidPrefix, setUuidPrefix] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [fromDate, setFromDate] = useState<Moment | null>(null);
  const [toDate, setToDate] = useState<Moment | null>(null);

  // Applied state (updated only when 絞り込み is clicked; drives fetch)
  const [appliedStatus, setAppliedStatus] = useState<string | null>(null);
  const [appliedUuidPrefix, setAppliedUuidPrefix] = useState<string>('');
  const [appliedOrganizationName, setAppliedOrganizationName] = useState<string>('');
  const [appliedFromDate, setAppliedFromDate] = useState<Moment | null>(null);
  const [appliedToDate, setAppliedToDate] = useState<Moment | null>(null);

  const fetchApplications = (
    page: number,
    status: string | null,
    uuidPfx: string,
    orgName: string,
    fromDt: Moment | null,
    toDt: Moment | null
  ) => {
    const params: Record<string, unknown> = { page };
    if (status) params.status = status;
    if (uuidPfx) params.uuid_prefix = uuidPfx;
    if (orgName) params.organization_name = orgName;
    if (fromDt) params.from = fromDt;
    if (toDt) params.to = toDt;
    getOrganizationApplications(toQueryParams(params, true)).then((res) => {
      setApplications(res);
    });
  };

  useEffect(() => {
    if (isAllowed) {
      fetchApplications(
        1,
        appliedStatus,
        appliedUuidPrefix,
        appliedOrganizationName,
        appliedFromDate,
        appliedToDate
      );
    }
  }, [
    appliedStatus,
    appliedUuidPrefix,
    appliedOrganizationName,
    appliedFromDate,
    appliedToDate,
    isAllowed,
  ]);

  const onChangePage = (page: number) => {
    fetchApplications(
      page,
      appliedStatus,
      appliedUuidPrefix,
      appliedOrganizationName,
      appliedFromDate,
      appliedToDate
    );
  };

  const onChangeStatusFilter = (option: StatusOption | null) => {
    if (option) {
      setSelectedStatus(option);
    }
  };

  const onClickFilter = () => {
    setAppliedStatus(selectedStatus.value);
    setAppliedUuidPrefix(uuidPrefix);
    setAppliedOrganizationName(organizationName);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const onClickDownloadCsv = () => {
    const params: Record<string, unknown> = {};
    if (appliedStatus) params.status = appliedStatus;
    if (appliedUuidPrefix) params.uuid_prefix = appliedUuidPrefix;
    if (appliedOrganizationName) params.organization_name = appliedOrganizationName;
    if (appliedFromDate) params.from = appliedFromDate;
    if (appliedToDate) params.to = appliedToDate;
    fetchOrganizationApplicationsCSV(toQueryParams(params, true)).then((res) => {
      downloadCsvFile(res.url);
    });
  };

  if (!isAllowed) {
    return (
      <Wrapper>
        <BreadCrumb crumbs={CRUMBS} />
        <PageHeader>決済加盟店申請管理</PageHeader>
        <NotAllowedMessage>
          選択されたマネーはこの機能を使用できません。他のマネーを選択してください。
        </NotAllowedMessage>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>加盟組織申請検索</Headings.h5>
      <SelectedPrivateMoneyDisplay />
      <Row>
        <FilterRow>
          <FilterItem>
            <Label>進捗状態</Label>
            <SelectWrapper>
              <Select<StatusOption, false, never>
                fullWidth
                options={STATUS_OPTIONS}
                value={selectedStatus}
                onChange={onChangeStatusFilter}
              />
            </SelectWrapper>
          </FilterItem>
          <FilterItem>
            <Label>申請番号</Label>
            <Input
              value={uuidPrefix}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUuidPrefix(e.target.value)}
            />
          </FilterItem>
          <FilterItem>
            <Label>申請受付日</Label>
            <DateTimeRange
              dateRange={{ from: fromDate, to: toDate }}
              setDateRange={({ from, to }) => {
                setFromDate(from ?? null);
                setToDate(to ?? null);
              }}
              wrapperStyles="margin-bottom: 0;"
              dateTimeProps={{
                fromProps: { showIcon: true },
                toProps: { showIcon: true },
              }}
            />
          </FilterItem>
          <FilterItem>
            <Label>組織名</Label>
            <Input
              value={organizationName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOrganizationName(e.target.value)
              }
            />
          </FilterItem>
        </FilterRow>
        <Button type="button" onClick={onClickFilter}>
          検索
        </Button>
      </Row>
      <SearchResultInfo>
        <SearchResultTitle>検索結果</SearchResultTitle>
        {applications && !!applications.count && (
          <>
            <SearchResultCount>件数：{applications.count}</SearchResultCount>
            <Button onClick={onClickDownloadCsv} variant="muted-outline">
              <FA icon="download" /> CSV
            </Button>
          </>
        )}
      </SearchResultInfo>
      {applications != null ? (
        <ListTable
          applications={applications.rows}
          pagination={applications.pagination}
          onChangePage={onChangePage}
        />
      ) : null}
    </Wrapper>
  );
};

export default MerchantApplications;
